import React, { useState } from "react";
import { HStack, Button, VStack, Textarea } from "@chakra-ui/react";
import { FaEye, FaCheck, FaFilePen, FaBoxesPacking } from "react-icons/fa6";
import {
  DialogRoot,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogCloseTrigger,
} from "@chakra-ui/react";
import {
  showSuccessToast,
  showErrorToast,
} from "@hydrologiq/shared/utils/toast";

interface AlertTriageActionsProps {
  alertId: string;
  currentStatus: "new" | "acknowledged" | "attended" | "completed";
  onUpdateStatus: (
    status: "new" | "acknowledged" | "attended" | "completed",
    note?: string
  ) => Promise<{ success: boolean; error?: any }>;
  onAddNote: (note: string) => Promise<{ success: boolean; error?: any }>;
  onArchive: () => Promise<{ success: boolean; error?: any }>;
  updatingStatus?: boolean;
  addingNote?: boolean;
  archiving?: boolean;
}

export function AlertTriageActions({
  currentStatus,
  onUpdateStatus,
  onAddNote,
  onArchive,
  updatingStatus,
  addingNote,
  archiving,
}: AlertTriageActionsProps) {
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [note, setNote] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const [pendingStatus, setPendingStatus] = useState<"acknowledged" | "attended" | "completed">(
    "acknowledged"
  );

  const handleAttend = async () => {
    const result = await onUpdateStatus(
      "acknowledged",
      statusNote.trim() || undefined
    );
    if (result.success) {
      showSuccessToast("Alert Acknowledged", "Alert status updated to acknowledged");
      setStatusDialogOpen(false);
      setStatusNote("");
    } else {
      showErrorToast("Failed to Update Alert", "Could not update alert status");
    }
  };

  const handleComplete = async () => {
    const result = await onUpdateStatus(
      "completed",
      statusNote.trim() || undefined
    );
    if (result.success) {
      showSuccessToast("Alert Completed", "Alert has been marked as completed");
      setStatusDialogOpen(false);
      setStatusNote("");
    } else {
      showErrorToast(
        "Failed to Complete Alert",
        "Could not mark alert as completed"
      );
    }
  };

  const handleAddNote = async () => {
    if (!note.trim()) return;

    const result = await onAddNote(note.trim());
    if (result.success) {
      showSuccessToast("Note Added", "Note has been added to the alert");
      setNoteDialogOpen(false);
      setNote("");
    } else {
      showErrorToast("Failed to Add Note", "Could not add note to alert");
    }
  };

  const handleArchive = async () => {
    const result = await onArchive();
    if (result.success) {
      showSuccessToast("Alert Archived", "Alert has been archived");
      setArchiveDialogOpen(false);
    } else {
      showErrorToast("Failed to Archive Alert", "Could not archive alert");
    }
  };

  const handleStatusAction = async () => {
    if (pendingStatus === "acknowledged") {
      await handleAttend();
    } else {
      await handleComplete();
    }
  };

  return (
    <HStack gap={2} wrap="wrap">
      {/* Attend/Complete Actions */}
      {currentStatus === "new" && (
        <DialogRoot
          open={statusDialogOpen}
          onOpenChange={({ open }) => setStatusDialogOpen(open)}
        >
          <DialogTrigger asChild>
            <Button
              variant="hydrologiq_primary"
              leftIcon={<FaEye />}
              onClick={() => setPendingStatus("acknowledged")}
              loading={updatingStatus}
            >
              Attend
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Attend Alert</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <VStack gap={4} align="stretch">
                <Textarea
                  placeholder="Add a note about attending this alert (optional)..."
                  value={statusNote}
                  onChange={(e) => setStatusNote(e.target.value)}
                  rows={3}
                />
              </VStack>
            </DialogBody>
            <DialogFooter>
              <DialogCloseTrigger asChild>
                <Button variant="outline">Cancel</Button>
              </DialogCloseTrigger>
              <Button
                variant="hydrologiq_primary"
                onClick={handleStatusAction}
                loading={updatingStatus}
              >
                Attend Alert
              </Button>
            </DialogFooter>
          </DialogContent>
        </DialogRoot>
      )}

      {currentStatus === "acknowledged" && (
        <DialogRoot
          open={statusDialogOpen}
          onOpenChange={({ open }) => setStatusDialogOpen(open)}
        >
          <DialogTrigger asChild>
            <Button
              variant="hydrologiq_primary"
              leftIcon={<FaCheck />}
              onClick={() => setPendingStatus("completed")}
              loading={updatingStatus}
            >
              Complete
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Complete Alert</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <VStack gap={4} align="stretch">
                <Textarea
                  placeholder="Add a note about completing this alert (optional)..."
                  value={statusNote}
                  onChange={(e) => setStatusNote(e.target.value)}
                  rows={3}
                />
              </VStack>
            </DialogBody>
            <DialogFooter>
              <DialogCloseTrigger asChild>
                <Button variant="outline">Cancel</Button>
              </DialogCloseTrigger>
              <Button
                variant="hydrologiq_primary"
                onClick={handleStatusAction}
                loading={updatingStatus}
              >
                Complete Alert
              </Button>
            </DialogFooter>
          </DialogContent>
        </DialogRoot>
      )}

      {/* Add Note Action */}
      {currentStatus !== "completed" && (
        <DialogRoot
          open={noteDialogOpen}
          onOpenChange={({ open }) => setNoteDialogOpen(open)}
        >
          <DialogTrigger asChild>
            <Button
              variant="hydrologiq_secondary"
              leftIcon={<FaFilePen />}
              loading={addingNote}
            >
              Add Note
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Note</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <VStack gap={4} align="stretch">
                <Textarea
                  placeholder="Add a note to this alert..."
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={4}
                />
              </VStack>
            </DialogBody>
            <DialogFooter>
              <DialogCloseTrigger asChild>
                <Button variant="outline">Cancel</Button>
              </DialogCloseTrigger>
              <Button
                variant="hydrologiq_primary"
                onClick={handleAddNote}
                loading={addingNote}
                disabled={!note.trim()}
              >
                Add Note
              </Button>
            </DialogFooter>
          </DialogContent>
        </DialogRoot>
      )}

      {/* Archive Action */}
      <DialogRoot
        open={archiveDialogOpen}
        onOpenChange={({ open }) => setArchiveDialogOpen(open)}
      >
        <DialogTrigger asChild>
          <Button
            variant="hydrologiq_secondary"
            leftIcon={<FaBoxesPacking />}
            loading={archiving}
          >
            Archive
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive Alert</DialogTitle>
          </DialogHeader>
          <DialogBody>
            Are you sure you want to archive this alert? This action cannot be
            undone.
          </DialogBody>
          <DialogFooter>
            <DialogCloseTrigger asChild>
              <Button variant="outline">Cancel</Button>
            </DialogCloseTrigger>
            <Button
              variant="hydrologiq_danger"
              onClick={handleArchive}
              loading={archiving}
            >
              Archive Alert
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </HStack>
  );
}

export default AlertTriageActions;
